'use strict';
import { SEARCH_BASE, ECOM_SEARCH_BASE, ECOM_ZOOVU_SEARCH_BASE, ECOM_DEV_SEARCH_BASE, ECOM_STAGE_SEARCH_BASE } from '../siteSearch/api/ApiConfig';
import { resolveLabels } from './labels';
/**
 * Avatar runtime configuration & constants.
 *
 * The avatar opens a realtime session + fetches the avatar config from a backend
 * endpoint resolved from the central {@link ApiConfig} bases for the current
 * environment (the same way the rest of the plugin resolves its API host),
 * honouring the plugin's `baseUrl` override when set. No API key is required.
 */
/**
 * Default CDN that hosts the avatar runtime assets (no trailing slash):
 *  - worklets + lip-sync model directly under this base
 *    (`audio-processor.js`, `audio-playback-processor.js`,
 *     `headworklet.min.mjs`, `headaudio.min.mjs`, `model-en-mixed.bin`)
 *  - avatar GLBs under `<assetBase>/avatars/`
 *
 * Separate from the backend endpoint: the CDN serves permissive CORS for the
 * cross-origin AudioWorklet / ESM-import / fetch loads the avatar needs.
 */
export const DEFAULT_ASSET_CDN = 'https://cdn.search.zoovu.com/assets';
/**
 * CDN modules for the 3D avatar (TalkingHead) renderer. These are loaded at
 * runtime via an injected import map + module script (never bundled), matching
 * the original widget. Versions are kept in sync with the proven widget build.
 */
export const CDN = {
    three: 'https://cdn.jsdelivr.net/npm/three@0.180.0/build/three.module.js/+esm',
    threeAddons: 'https://cdn.jsdelivr.net/npm/three@0.180.0/examples/jsm/',
    talkingHead: 'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@1.6/modules/talkinghead.mjs',
    // LiveKit client for the HeyGen (streaming-video) renderer.
    liveKit: 'https://cdn.jsdelivr.net/npm/livekit-client@2.7.0/dist/livekit-client.esm.mjs'
};
const VALID_SIZES = ['s', 'm', 'l'];
const VALID_POSITIONS = [
    'top', 'bottom', 'left', 'right',
    'top-left', 'top-right', 'bottom-left', 'bottom-right'
];
const VALID_LAYOUTS = ['stage', 'chat'];
const DEFAULT_SIZE = 'm';
const DEFAULT_POSITION = 'bottom-right';
/** `stage` is the original presentation - every existing integration keeps it without opting in. */
const DEFAULT_LAYOUT = 'stage';
/** Strip a trailing slash from a URL/origin. */
export function trimTrailingSlash(value) {
    return (value || '').replace(/\/+$/, '');
}
/**
 * Resolve the default backend endpoint from the central {@link ApiConfig} bases
 * for the current environment — mirroring how the plugin selects its API host
 * (dev / stage / prod, ss360 vs zoovu). Honours the plugin `baseUrl` override.
 */
function resolveDefaultEndpoint(opts) {
    // avatarBaseUrl targets the avatar service only (search is unaffected).
    if (opts.avatarBaseUrl)
        return opts.avatarBaseUrl;
    // An explicit baseUrl on the plugin config wins (mirrors the rest of the plugin).
    if (opts.baseUrl)
        return opts.baseUrl;
    if (opts.ecomDev)
        return ECOM_DEV_SEARCH_BASE;
    if (opts.ecomStage)
        return ECOM_STAGE_SEARCH_BASE;
    if (opts.ecom)
        return opts.isZoovu ? ECOM_ZOOVU_SEARCH_BASE : ECOM_SEARCH_BASE;
    return opts.isZoovu ? ECOM_ZOOVU_SEARCH_BASE : SEARCH_BASE;
}
/**
 * Validate & normalize the public {@link AvatarConfiguration} into a
 * {@link ResolvedAvatarConfig}. Invalid `size`/`position` values fall back to
 * the defaults (with a console warning) rather than throwing, so a typo never
 * breaks the host page.
 *
 * @param raw the user-supplied avatarConfig (already merged with DefaultConfig)
 * @param opts environment/context inputs (siteId, language, ecom flags, flavour)
 * @returns the resolved config, or `null` when no `id` is present
 */
export function resolveAvatarConfig(raw, opts = {}) {
    if (raw === undefined || raw === null || !raw.id) {
        return null;
    }
    let size = raw.size;
    if (VALID_SIZES.indexOf(size) === -1) {
        if (raw.size !== undefined) {
            console.warn(`[avatar] Invalid avatarConfig.size "${raw.size}", falling back to "${DEFAULT_SIZE}".`);
        }
        size = DEFAULT_SIZE;
    }
    let position = raw.position;
    if (VALID_POSITIONS.indexOf(position) === -1) {
        if (raw.position !== undefined) {
            console.warn(`[avatar] Invalid avatarConfig.position "${raw.position}", falling back to "${DEFAULT_POSITION}".`);
        }
        position = DEFAULT_POSITION;
    }
    let layout = raw.layout;
    if (VALID_LAYOUTS.indexOf(layout) === -1) {
        if (raw.layout !== undefined) {
            console.warn(`[avatar] Invalid avatarConfig.layout "${raw.layout}", falling back to "${DEFAULT_LAYOUT}".`);
        }
        layout = DEFAULT_LAYOUT;
    }
    const endpoint = trimTrailingSlash(resolveDefaultEndpoint(opts));
    const assetBase = trimTrailingSlash(DEFAULT_ASSET_CDN);
    const avatarBase = trimTrailingSlash(`${assetBase}/avatars`);
    // The backend routes are project-scoped and always use the search siteId.
    const projectId = String(opts.siteId != null ? opts.siteId : '').trim();
    if (!projectId) {
        console.error('[avatar] No siteId configured — the project-scoped backend routes require it.');
    }
    const labels = resolveLabels(opts.language, raw.labels);
    return {
        id: String(raw.id),
        projectId,
        size,
        position,
        layout,
        endpoint,
        assetBase,
        avatarBase,
        labels
    };
}
